import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { getProjectById, getLinkedSOWIdsByProjectId, linkSOWsToProject } from "../../http/requests/project-admin";
import { getSOWDetails } from "../../http/requests/sow";
import { getEmployeeDropdownData } from "../../http/requests/session";
import { getAllClients, getListItems } from "../../http/requests/dropdown";
import { updateProjectAdminData } from "../../redux/actions/project-admin";
import { getApproversIds } from "../../common/utils/datatable";
import { toastOptions } from "../../common/utils/toasterOptions";

const formatDate = (d) => {
  if (!d) return "";
  if (typeof d === "string") return d.split("T")[0] || d;
  return d;
};

const empId = (e) => (e?.id ?? e?.azureId ?? e?.AzureId ?? e?.employeeId ?? e?.EmployeeId ?? "").toString().trim();
const empLabel = (e) => {
  if (!e) return "";
  const name = e.name ?? e.Name;
  if (name) return name;
  const full = `${e.firstName ?? ""} ${e.lastName ?? ""}`.trim();
  if (full) return full;
  return e.email ?? e.Email ?? "";
};
const clientId = (c) => c?.id ?? c?.Id;
const clientLabel = (c) => c?.name ?? c?.Name ?? "";
const itemId = (o) => o?.id ?? o?.Id;
const shortCode = (o) => o?.shortCodeValue ?? o?.ShortCodeValue ?? "";

const ProjectEditModal = ({ open, onClose, projectRow, pageNo, pageSize, expiredAllocations, onSuccess }) => {
  const dispatch = useDispatch();
  const projectId = projectRow?.projectId ?? projectRow?.ProjectId;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [project, setProject] = useState(null);
  const [formData, setFormData] = useState(null);
  const [clients, setClients] = useState([]);
  const [typeOptions, setTypeOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [allSOWs, setAllSOWs] = useState([]);

  useEffect(() => {
    if (!open || !projectId) return;
    const load = async () => {
      setLoading(true);
      try {
        const [projectRes, clientsRes, typeRes, statusRes, employeesRes, sowRes, linkedSOWIdsRes] = await Promise.all([
          getProjectById(projectId),
          getAllClients(),
          getListItems("projectType"),
          getListItems("projectStatus"),
          getEmployeeDropdownData(),
          getSOWDetails(1, 500, "ASC", "sowName", ""),
          getLinkedSOWIdsByProjectId(projectId),
        ]);
        // API returns project object; axios puts it in response.data
        const p = projectRes?.data ?? projectRes;
        if (!p || (p.projectId == null && p.ProjectId == null)) {
          toast.error("Project not found", toastOptions);
          onClose();
          return;
        }
        setProject(p);
        const clientsList = Array.isArray(clientsRes) ? clientsRes : clientsRes?.data ?? [];
        const typeList = Array.isArray(typeRes) ? typeRes : typeRes?.data ?? [];
        const statusList = Array.isArray(statusRes) ? statusRes : statusRes?.data ?? [];
        const empList = Array.isArray(employeesRes) ? employeesRes : employeesRes?.data ?? [];
        setClients(clientsList);
        setTypeOptions(typeList);
        setStatusOptions(statusList);
        setEmployees(empList);

        const rawSowData = sowRes?.data ?? sowRes?.Data ?? [];
        const sowList = Array.isArray(rawSowData) ? rawSowData : [];
        const activeSOWs = sowList
          .filter((s) => (s?.active ?? s?.Active) !== false && (s?.active ?? s?.Active) !== 0)
          .map((s) => ({
            sowId: s?.sowId ?? s?.SOWId,
            sowName: s?.sowName ?? s?.SOWName ?? "",
          }))
          .filter((s) => s.sowId != null);
        setAllSOWs(activeSOWs);

        const linkedIds = linkedSOWIdsRes?.data ?? linkedSOWIdsRes ?? [];
        const linkedIdList = Array.isArray(linkedIds) ? linkedIds : [];
        const preSelectedSOWs = linkedIdList
          .map((id) => activeSOWs.find((s) => s.sowId === id))
          .filter(Boolean);

        const approversRaw = p?.approvers ?? p?.Approvers ?? [];
        const approversList = Array.isArray(approversRaw) ? approversRaw : [];
        const approvers = approversList.map((a) => ({
          approverId: a?.approverId ?? a?.ApproverId ?? a?.id ?? a?.Id ?? "",
          approverName: a?.approverName ?? a?.ApproverName ?? a?.name ?? a?.Name ?? "",
          approverEmail: a?.approverEmail ?? a?.ApproverEmail ?? a?.email ?? a?.Email ?? "",
        }));

        const rawClientId = p?.clientId ?? p?.ClientId;
        const clientIdValue = rawClientId != null && rawClientId !== "" ? Number(rawClientId) : "";
        const rawTypeId = p?.typeId ?? p?.TypeId;
        let typeVal = p?.type ?? p?.Type ?? "";
        if (!typeVal && rawTypeId != null && typeList.length) {
          const typeOpt = typeList.find((o) => itemId(o) === rawTypeId || itemId(o) === Number(rawTypeId));
          if (typeOpt) typeVal = shortCode(typeOpt);
        }
        setFormData({
          projectName: p?.projectName ?? p?.ProjectName ?? "",
          clientId: !Number.isNaN(clientIdValue) ? clientIdValue : "",
          clientName: p?.clientName ?? p?.ClientName ?? "",
          linkedSOWNames: p?.linkedSOWNames ?? p?.LinkedSOWNames ?? "",
          linkedSOWs: preSelectedSOWs,
          type: typeVal,
          typeId: rawTypeId ?? "",
          sowStartDate: p?.sowStartDate ?? p?.SOWStartDate ? dayjs(formatDate(p.sowStartDate ?? p.SOWStartDate)) : null,
          sowEndDate: p?.sowEndDate ?? p?.SOWEndDate ? dayjs(formatDate(p.sowEndDate ?? p.SOWEndDate)) : null,
          projectManagerID: p?.projectManagerId ?? p?.ProjectManagerId ?? "",
          projectManagerName: p?.projectManagerName ?? p?.ProjectManagerName ?? "",
          maxBillableCount: p?.maxBillableCount ?? p?.MaxBillableCount ?? null,
          status: p?.status ?? p?.Status ?? "",
          approvers,
        });
      } catch (err) {
        toast.error(err?.response?.data?.errorMessage || err?.message || "Failed to load project", toastOptions);
        onClose();
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [open, projectId, onClose]);

  const handleChange = (field, value) => {
    setFormData((prev) => (prev ? { ...prev, [field]: value } : null));
  };

  const handleManagerChange = (_, option) => {
    if (!option) {
      handleChange("projectManagerID", "");
      handleChange("projectManagerName", "");
      return;
    }
    const id = empId(option);
    const label = empLabel(option);
    handleChange("projectManagerID", id);
    handleChange("projectManagerName", label);
  };

  const handleApproversChange = (_, selectedOptions) => {
    const approvers = (selectedOptions || []).map((opt) => {
      const id = empId(opt);
      return {
        approverId: id,
        approverName: empLabel(opt),
        approverEmail: (opt?.email ?? opt?.Email ?? "").toString(),
      };
    }).filter((a) => a.approverId);
    handleChange("approvers", approvers);
  };

  const handleClientChange = (_, option) => {
    if (!option) {
      handleChange("clientId", "");
      handleChange("clientName", "");
      return;
    }
    handleChange("clientId", clientId(option));
    handleChange("clientName", clientLabel(option));
  };

  const handleSOWsChange = (_, options) => {
    handleChange("linkedSOWs", Array.isArray(options) ? options : []);
  };

  const validate = () => {
    if (!formData?.projectName?.trim()) {
      toast.error("Project Name is required", toastOptions);
      return false;
    }
    const clientIdNum = formData?.clientId != null && formData?.clientId !== ""
      ? Number(formData.clientId)
      : NaN;
    if (Number.isNaN(clientIdNum) || clientIdNum <= 0) {
      toast.error("Client is required", toastOptions);
      return false;
    }
    if (!formData?.type && !formData?.typeId) {
      toast.error("Type is required", toastOptions);
      return false;
    }
    if (!formData?.sowStartDate || !formData?.sowEndDate) {
      toast.error("SOW Start Date and End Date are required", toastOptions);
      return false;
    }
    const start = dayjs(formData.sowStartDate);
    const end = dayjs(formData.sowEndDate);
    if (end.isBefore(start)) {
      toast.error("SOW End Date must be on or after Start Date", toastOptions);
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!formData || !validate()) return;
    const effectiveProjectId = project?.projectId ?? project?.ProjectId ?? projectRow?.projectId ?? projectRow?.ProjectId;
    if (effectiveProjectId == null || effectiveProjectId === "") {
      toast.error("Project ID is missing. Please close and try again.", toastOptions);
      return;
    }
    const clientIdNum = formData.clientId != null && formData.clientId !== ""
      ? Number(formData.clientId)
      : NaN;
    if (Number.isNaN(clientIdNum) || clientIdNum <= 0) {
      toast.error("Client is required", toastOptions);
      return;
    }
    setSaving(true);
    try {
      const validApprovers = (formData.approvers || []).filter((a) => a?.approverId != null && String(a.approverId).trim() !== "");
      const approverIdsStr = getApproversIds(validApprovers);
      const payload = {
        projectId: effectiveProjectId,
        projectName: formData.projectName.trim(),
        clientId: clientIdNum,
        clientName: formData.clientName ?? "",
        type: formData.type ?? "",
        typeId: formData.typeId || (typeOptions.find((o) => (o?.shortCodeValue ?? o?.ShortCodeValue) === formData.type)?.id ?? typeOptions.find((o) => o?.id === formData.typeId)?.id),
        sowStartDate: formData.sowStartDate ? dayjs(formData.sowStartDate).format("YYYY-MM-DD") : null,
        sowEndDate: formData.sowEndDate ? dayjs(formData.sowEndDate).format("YYYY-MM-DD") : null,
        projectManagerID: formData.projectManagerID ?? "",
        projectManagerName: formData.projectManagerName ?? "",
        projectManagerId: formData.projectManagerID ?? "",
        maxBillableCount: formData.maxBillableCount != null && formData.maxBillableCount !== "" ? Number(formData.maxBillableCount) : null,
        status: formData.status?.trim() || null,
        projectApprovers: approverIdsStr,
        pageNo: pageNo ?? 0,
        rows: pageSize ?? 100,
        expiredAllocations: !!expiredAllocations,
      };
      dispatch(updateProjectAdminData(payload));
      const linkedSOWs = formData.linkedSOWs ?? [];
      const sowIds = linkedSOWs.map((s) => s?.sowId ?? s?.SOWId).filter(Boolean);
      await linkSOWsToProject(effectiveProjectId, sowIds);
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.errorMessage || err?.message || "Failed to update project", toastOptions);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setFormData(null);
    setProject(null);
    setAllSOWs([]);
    onClose();
  };

  const selectedManager = formData?.projectManagerID
    ? employees.find((e) => empId(e) === formData.projectManagerID) ?? { id: formData.projectManagerID, name: formData.projectManagerName }
    : null;

  const selectedApprovers = (formData?.approvers || []).map((a) => {
    const emp = employees.find((e) => empId(e) === a.approverId);
    return emp ?? { id: a.approverId, name: a.approverName, email: a.approverEmail };
  });

  const selectedClient = formData?.clientId
    ? clients.find((c) => clientId(c) === formData.clientId || clientId(c) === Number(formData.clientId))
    : null;

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth={false}
      PaperProps={{
        sx: {
          borderRadius: "12px",
          padding: "8px",
          minWidth: 500,
          maxWidth: "min(864px, 90vw)",
          margin: "24px",
          maxHeight: "calc(100vh - 48px)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: 600, color: "#1773bc", pb: 1 }}>Edit Project</DialogTitle>
      <DialogContent dividers sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 200 }}>
            <CircularProgress />
          </Box>
        ) : formData ? (
          <>
            <TextField
              label="Project Name"
              value={formData.projectName ?? ""}
              onChange={(e) => handleChange("projectName", e.target.value)}
              fullWidth
              required
              size="small"
            />
            <Autocomplete
              options={clients}
              getOptionLabel={(c) => clientLabel(c) || ""}
              isOptionEqualToValue={(a, b) => clientId(a) === clientId(b)}
              value={selectedClient}
              onChange={handleClientChange}
              renderInput={(params) => <TextField {...params} label="Client" required size="small" />}
            />
            <Autocomplete
              multiple
              options={allSOWs}
              getOptionLabel={(s) => (s?.sowName ?? s?.SOWName ?? "").toString()}
              isOptionEqualToValue={(a, b) => (a?.sowId ?? a?.SOWId) === (b?.sowId ?? b?.SOWId)}
              value={formData.linkedSOWs ?? []}
              onChange={handleSOWsChange}
              blurOnSelect
              renderInput={(params) => (
                <TextField {...params} label="Linked SOWs" size="small" placeholder="Select SOWs" />
              )}
            />
            <FormControl fullWidth size="small" required>
              <InputLabel>Type</InputLabel>
              <Select
                value={formData.type ?? ""}
                label="Type"
                onChange={(e) => {
                  const opt = typeOptions.find((o) => shortCode(o) === e.target.value || itemId(o) === e.target.value);
                  handleChange("type", e.target.value);
                  handleChange("typeId", opt ? itemId(opt) : "");
                }}
              >
                {typeOptions.map((opt) => (
                  <MenuItem key={itemId(opt)} value={shortCode(opt)}>
                    {shortCode(opt)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="SOW Start Date"
                value={formData.sowStartDate}
                onChange={(d) => handleChange("sowStartDate", d)}
                renderInput={(params) => <TextField {...params} fullWidth size="small" required />}
              />
              <DatePicker
                label="SOW End Date"
                value={formData.sowEndDate}
                onChange={(d) => handleChange("sowEndDate", d)}
                renderInput={(params) => <TextField {...params} fullWidth size="small" required />}
              />
            </LocalizationProvider>
            <Autocomplete
              options={employees}
              getOptionLabel={(e) => empLabel(e) || ""}
              isOptionEqualToValue={(a, b) => empId(a) === empId(b)}
              value={selectedManager}
              onChange={handleManagerChange}
              renderInput={(params) => <TextField {...params} label="Veersa Project Manager" size="small" />}
            />
            <TextField
              label="Max Billable Count"
              type="number"
              value={formData.maxBillableCount ?? ""}
              onChange={(e) => handleChange("maxBillableCount", e.target.value === "" ? null : e.target.value)}
              fullWidth
              size="small"
              inputProps={{ min: 0 }}
            />
            <Autocomplete
              multiple
              options={employees}
              getOptionLabel={(e) => empLabel(e) || ""}
              isOptionEqualToValue={(a, b) => empId(a) === empId(b)}
              value={selectedApprovers}
              onChange={handleApproversChange}
              renderInput={(params) => <TextField {...params} label="Approvers" size="small" placeholder="Select approvers" />}
            />
          </>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || loading || !formData} sx={{ backgroundColor: "#1773bc" }}>
          {saving ? "Saving..." : "Update"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProjectEditModal;
